// ============================================================
// Role capabilities
//
// A single user can legitimately hold more than one role: everyone
// can be evaluated (Staff), and admins OR anyone who evaluates others
// (e.g. a department chair assigned as an evaluator) can act as an
// evaluator (Admin). The UI role switch is only meaningful — and only
// shown — when a user actually holds more than one role.
//
// This mirrors the database authorization model (i_am_admin /
// i_am_evaluator); it never grants access on its own, it only decides
// which modes to surface in the UI.
// ============================================================
import type { Profile, UserRole } from './types'

export type AppRole = 'teacher' | 'admin'

export const ADMIN_ROLES: UserRole[] = ['district_admin', 'building_admin']

export interface Capabilities {
  canStaff: boolean
  canAdmin: boolean
  roles: AppRole[]
}

/**
 * Derive which UI modes a user may use.
 * @param profile               the signed-in user's profile (null when unknown)
 * @param evaluatorAssignments  how many staff this user evaluates (non-admin evaluators)
 */
export function deriveCapabilities(
  profile: Profile | null,
  evaluatorAssignments = 0
): Capabilities {
  // Everyone can own/own a plan and be evaluated.
  const canStaff = true
  const isAdminRole = !!profile && ADMIN_ROLES.includes(profile.role)
  const canAdmin = isAdminRole || evaluatorAssignments > 0

  const roles: AppRole[] = []
  if (canStaff) roles.push('teacher')
  if (canAdmin) roles.push('admin')

  return { canStaff, canAdmin, roles }
}

/** The role a user should land in by default (evaluators land in Admin). */
export function defaultRole(caps: Capabilities): AppRole {
  return caps.canAdmin ? 'admin' : 'teacher'
}

/** Whether the Staff/Admin switch should be offered at all. */
export function canSwitchRoles(caps: Capabilities): boolean {
  return caps.canStaff && caps.canAdmin
}
